import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { canEditAdsGuidance } from "@/lib/adsAssistantPermissions";
import type { AdsFeedImportLocationRow } from "@/components/ads-assistant/adsFeedImportSectionTypes";

/** Feed-import location list: sales see only assigned lots; trainer+ see all org locations. */
export function useAdsAssistantFeedImportLocations(
  orgLocations: AdsFeedImportLocationRow[],
  orgLocationsLoaded: boolean,
) {
  const auth = useAuth();
  const isGuidance = canEditAdsGuidance(auth);

  const locations = useMemo(() => {
    if (isGuidance || auth.status !== "ready") return orgLocations;
    const allowed = new Set((auth.accessibleLocationIds ?? []).map((id) => id.trim()).filter(Boolean));
    const preferred = auth.activeLocationId?.trim() || "";
    const filtered = orgLocations.filter((loc) => allowed.has(loc.id));
    if (filtered.length) return filtered;
    if (preferred && orgLocations.some((l) => l.id === preferred)) {
      return orgLocations.filter((l) => l.id === preferred);
    }
    return filtered;
  }, [orgLocations, isGuidance, auth]);

  const salesMustPickLocation = auth.status === "ready" && !isGuidance;

  const defaultLocationId = useMemo(() => {
    if (!orgLocationsLoaded || !locations.length) return "";
    if (locations.length === 1) return locations[0]!.id;
    const preferred = auth.activeLocationId?.trim() || "";
    if (preferred && locations.some((l) => l.id === preferred)) return preferred;
    return "";
  }, [locations, orgLocationsLoaded, auth.activeLocationId]);

  return { locations, salesMustPickLocation, isGuidance, defaultLocationId };
}
